"use client";

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from "react";

/* ==========================================================================
   TYPES
   ========================================================================== */

export type NotificationType = "info" | "success" | "warning" | "error";

/**
 * 'notification' — something new the user should review (incoming message, alarm).
 * 'event' — confirmation of a user-initiated action (note saved, clipboard copied).
 *            Events show a toast and appear in history but do NOT increment the
 *            unread badge or highlight as unread.
 */
export type NotificationCategory = "notification" | "event";

export interface Notification {
  id: string;
  title: string;
  message?: string;
  type: NotificationType;
  category: NotificationCategory;
  appName?: string;
  icon?: React.ReactElement;
  timestamp: number;
  read: boolean;
  /** Auto-dismiss after ms (default: 5000, set 0 to persist) */
  duration?: number;
  /** Optional click action */
  onClick?: () => void;
  /** If set, only one unread notification with this key can exist at a time.
   *  Re-sending with the same key updates the existing notification in place. */
  dedupKey?: string;
}

export interface SendNotificationOptions {
  title: string;
  message?: string;
  type?: NotificationType;
  /** Defaults to 'notification'. Use 'event' for user-initiated action confirmations. */
  category?: NotificationCategory;
  appName?: string;
  icon?: React.ReactElement;
  duration?: number;
  onClick?: () => void;
  /** If set, only one unread notification with this key can exist at a time.
   *  Re-sending with the same key updates the existing notification in place
   *  (new title/message/timestamp) instead of creating a duplicate. */
  dedupKey?: string;
}

export interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  sendNotification: (options: SendNotificationOptions) => string;
  dismissNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  /** Mark all unread notifications from a specific app as read */
  markAppAsRead: (appName: string) => void;
  clearAll: () => void;
}

/* ==========================================================================
   CONTEXT
   ========================================================================== */

const NotificationContext = createContext<NotificationContextValue | null>(
  null
);

/* ==========================================================================
   PROVIDER
   ========================================================================== */

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const nextIdRef = useRef(0);

  const sendNotification = useCallback(
    (options: SendNotificationOptions): string => {
      const id = `notif-${Date.now()}-${nextIdRef.current++}`;
      const category = options.category ?? "notification";

      setNotifications((prev) => {
        // Dedup: if a dedupKey is provided and an unread notification with
        // that key already exists, update it in place instead of adding a dup.
        if (options.dedupKey) {
          const idx = prev.findIndex(
            (n) => n.dedupKey === options.dedupKey && !n.read
          );
          if (idx !== -1) {
            const existing = prev[idx];
            const refreshed: Notification = {
              ...existing,
              title: options.title,
              message: options.message,
              type: options.type ?? existing.type,
              timestamp: Date.now(),
              onClick: options.onClick,
            };
            const next = [...prev];
            next.splice(idx, 1);
            return [refreshed, ...next];
          }
        }

        const notification: Notification = {
          id,
          title: options.title,
          message: options.message,
          type: options.type ?? "info",
          category,
          appName: options.appName,
          icon: options.icon,
          timestamp: Date.now(),
          read: category === "event",
          duration: options.duration,
          onClick: options.onClick,
          dedupKey: options.dedupKey,
        };
        return [notification, ...prev];
      });

      return id;
    },
    []
  );

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const markAppAsRead = useCallback((appName: string) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.appName === appName && !n.read ? { ...n, read: true } : n
      )
    );
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Clear all notifications on logout so stale data doesn't bleed between sessions
  useEffect(() => {
    const handler = () => setNotifications([]);
    window.addEventListener("desktop:logout", handler);
    return () => window.removeEventListener("desktop:logout", handler);
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        sendNotification,
        dismissNotification,
        markAsRead,
        markAllAsRead,
        markAppAsRead,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

/* ==========================================================================
   HOOKS
   ========================================================================== */

export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext);

  if (!context) {
    return {
      notifications: [],
      unreadCount: 0,
      sendNotification: () => {
        console.warn("useNotifications: called outside NotificationProvider");
        return "";
      },
      dismissNotification: () => {},
      markAsRead: () => {},
      markAllAsRead: () => {},
      markAppAsRead: () => {},
      clearAll: () => {},
    };
  }

  return context;
}
